import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Form, Input, List, Space, Switch, Tag, Typography, message } from "antd";
import { GlobalOutlined, SearchOutlined } from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import { olreadiaService } from "services/olreadiaService";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;

export default function WebsiteDomain() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const websiteId = searchParams.get("websiteId") || "";
  const suggestedName = searchParams.get("suggestedName") || "";
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState("");

  const search = async () => {
    const values = await form.validateFields();
    try {
      setLoading(true);
      setResult(await olreadiaService.domains.search(values.domain, ["com", "net", "org"], actorId));
    } catch (error) {
      message.error(error?.message || "Domain search failed.");
    } finally {
      setLoading(false);
    }
  };

  const checkout = async (domain) => {
    if (!selectedWorkspaceId || !websiteId) return;
    try {
      setCheckingOut(domain);
      await olreadiaService.domains.checkout({
        workspaceId: selectedWorkspaceId,
        websiteId,
        domain,
        periodYears: form.getFieldValue("periodYears") || 1,
        privacy: form.getFieldValue("privacy") !== false,
        autoRenew: form.getFieldValue("autoRenew") !== false,
      }, actorId);
      message.success("Domain checkout started.");
    } catch (error) {
      message.error(error?.message || "Could not start domain checkout.");
    } finally {
      setCheckingOut("");
    }
  };

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><GlobalOutlined /></span>
            <div>
              <Text className="olreadia-flow-eyebrow">Custom domain</Text>
              <Title level={2}>Search Domain</Title>
              <Paragraph>Find a domain for your Olreadia website and start checkout.</Paragraph>
            </div>
          </section>
          {!websiteId ? <Alert type="warning" showIcon message="Website required" description="Create or open a website before adding a custom domain." style={{ marginBottom: 12 }} /> : null}
          <Card className="olreadia-flow-card">
            <Form form={form} layout="vertical" initialValues={{ domain: suggestedName, periodYears: 1, privacy: true, autoRenew: true }}>
              <Form.Item name="domain" label="Domain name" rules={[{ required: true }]}>
                <Input placeholder="mybusiness" />
              </Form.Item>
              <Space wrap>
                <Form.Item name="periodYears" label="Years"><Input type="number" min={1} max={10} /></Form.Item>
                <Form.Item name="privacy" label="Privacy" valuePropName="checked"><Switch /></Form.Item>
                <Form.Item name="autoRenew" label="Auto renew" valuePropName="checked"><Switch /></Form.Item>
              </Space>
              <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={search}>Search</Button>
            </Form>
          </Card>
          {result ? (
            <Card className="olreadia-flow-card" style={{ marginTop: 12 }}>
              <Title level={4}>Results</Title>
              <List
                dataSource={[{ domain: result.requestedDomain, available: result.available, price: result.price, currency: result.currency }, ...(result.suggestions || [])]}
                renderItem={(item) => (
                  <List.Item actions={[
                    item.available ? <Button type="primary" loading={checkingOut === item.domain} onClick={() => checkout(item.domain)}>Checkout</Button> : null,
                  ]}>
                    <List.Item.Meta
                      title={<Space>{item.domain}<Tag color={item.available ? "green" : "red"}>{item.available ? "Available" : "Unavailable"}</Tag></Space>}
                      description={item.price ? `${item.currency || "USD"} ${item.price}` : "Price unavailable"}
                    />
                  </List.Item>
                )}
              />
            </Card>
          ) : null}
        </main>
        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>
    </div>
  );
}
